import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, refreshAccess, session } from "./api";
import { hasRole, type Role } from "./roles";
import type { Me } from "./types";

type Status = "loading" | "signed-out" | "signed-in";

interface AuthState {
  status: Status;
  user: Me | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (minimum: Role) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [status, setStatus] = useState<Status>(session.hasRefresh() ? "loading" : "signed-out");

  // Resume a session after a reload: refresh the access token, then ask who we are.
  useEffect(() => {
    if (!session.hasRefresh()) return;
    let cancelled = false;
    (async () => {
      const ok = await refreshAccess();
      const me = ok ? await api<Me>("/auth/me/").catch(() => null) : null;
      if (cancelled) return;
      setUser(me);
      setStatus(me ? "signed-in" : "signed-out");
      if (!me) session.clear();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The refresh token was rejected mid-session (expired, revoked, user deactivated).
  useEffect(
    () =>
      session.onExpired(() => {
        setUser(null);
        setStatus("signed-out");
      }),
    [],
  );

  const login = useCallback(async (username: string, password: string) => {
    const data = await api<{ access: string; refresh: string; user: Me }>("/auth/login/", {
      method: "POST",
      body: { username, password },
    });
    session.set(data.access, data.refresh);
    setUser(data.user);
    setStatus("signed-in");
  }, []);

  const logout = useCallback(async () => {
    const refresh = session.refreshToken();
    // Blacklist the refresh token server-side; sign out locally either way.
    if (refresh) await api("/auth/logout/", { method: "POST", body: { refresh } }).catch(() => undefined);
    session.clear();
  }, []);

  const can = useCallback((minimum: Role) => hasRole(user?.role, minimum), [user]);

  const value = useMemo(() => ({ status, user, login, logout, can }), [status, user, login, logout, can]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>.");
  return value;
}
